import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { NavbarProps, MenuItem, DropdownItem, MenuProps } from "@/types/navbar";
import HamburgerButton from "./shared/HamburgerButtons";
import { getMenuItems } from "./shared/menuItems";
import { defaultNavbarContent } from "./defaultNavbarContent";
import { trackEvent } from "@/components/analytics";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { useEscapeKey } from "./shared/useEscapeKey";

export default function NavBarModernClean({
  services,
  primaryCTA = defaultNavbarContent.primaryCTA,
  primaryCTAUrl = defaultNavbarContent.primaryCTAUrl,
  secondaryCTA = defaultNavbarContent.secondaryCTA,
  secondaryCTAUrl = defaultNavbarContent.secondaryCTAUrl,
  navbarSecondaryCTA,
  navbarSecondaryCTAUrl,
  logoUrl = defaultNavbarContent.logoUrl,
  enableResources = true,
  hasBanner = false,
  ...forwardedProps
}: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuItems = getMenuItems(services, enableResources);

  // Disable scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileOpen]);

  return (
    <div className="w-full" {...forwardedProps}>
      <div className="mx-auto max-w-7xl px-2 sm:px-4">
        <div className="relative flex h-[144px] items-center justify-center">
          {/* Main nav container */}
          <div
            className={`absolute left-0 right-0 top-0 mx-auto flex h-[120px] w-full max-w-6xl items-center justify-between gap-8 lg:gap-4 rounded-2xl border-t border-gray-200 bg-background px-8 py-5 shadow-lg ${hasBanner ? "" : "mt-6"}`}
          >
            {/* Logo — fills the box's padded height; width is capped so it never crowds the nav items */}
            <div className="flex h-full min-w-0 items-center">
              <Link to="/" className="flex h-full min-w-0 items-center" aria-label="Logo - go to homepage">
                <img
                  src={logoUrl}
                  alt="Logo"
                  fetchPriority="high"
                  className="h-full w-auto min-w-0 max-w-full object-contain text-primary lg:max-w-[16rem]"
                />
              </Link>
            </div>
            {/* Desktop Menu */}
            <NavBar menuItems={menuItems} />
            {/* CTA Button (always visible) */}
            <div className="flex items-center gap-4">
              {navbarSecondaryCTA && navbarSecondaryCTAUrl && (
                <div className="hidden md:flex">
                  <Button asChild variant="outline" websiteStyle="modernClean">
                    <Link
                      to={navbarSecondaryCTAUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackEvent("navbar_secondary_cta_click", { label: navbarSecondaryCTA })
                      }
                    >
                      {navbarSecondaryCTA}
                    </Link>
                  </Button>
                </div>
              )}
              <div className="hidden md:flex">
                <Button asChild variant="default" websiteStyle="modernClean">
                  <Link to={primaryCTAUrl}>{primaryCTA}</Link>
                </Button>
              </div>
              {/* Hamburger for mobile and tablet */}
              <div className="flex items-center lg:hidden">
                <HamburgerButton isOpen={mobileOpen} toggleMenu={() => setMobileOpen(v => !v)} />
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Menu Overlay */}
        {mobileOpen && (
          <MobileMenu
            menuItems={menuItems}
            setMobileOpen={setMobileOpen}
            primaryCTA={primaryCTA}
            primaryCTAUrl={primaryCTAUrl}
            navbarSecondaryCTA={navbarSecondaryCTA}
            navbarSecondaryCTAUrl={navbarSecondaryCTAUrl}
          />
        )}
      </div>
    </div>
  );
}

{
  /* TODO: Check to see if we can reuse in other navbar components */
}
function NavBar({ menuItems }: { menuItems: MenuItem[] }) {
  return (
    <nav className="hidden min-w-0 flex-1 items-center justify-center lg:flex lg:px-4">
      <NavigationMenu>
        <NavigationMenuList>
          {menuItems.map(item =>
            item.isDropdown && item.dropdownItems ? (
              <NavigationMenuItem key={item.label}>
                <NavigationMenuTrigger
                  href={item.href}
                  className="inline-flex h-auto w-max items-center justify-center rounded-md px-4 py-2 text-lg font-semibold text-foreground hover:bg-primary/30 hover:text-accent-foreground"
                >
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[200px] rounded-md shadow-lg">
                  <ul className="flex flex-col py-2">
                    {item.dropdownItems.map(dropdownItem => (
                      <li key={dropdownItem.slug}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={dropdownItem.href}
                            className="block rounded-md px-6 py-2 text-base text-foreground hover:bg-primary/30 hover:text-accent-foreground"
                          >
                            {dropdownItem.name}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            ) : (
              <NavigationMenuItem key={item.label}>
                <NavigationMenuLink asChild>
                  <Link
                    to={item.href}
                    className="inline-flex h-auto w-max items-center justify-center rounded-md px-4 py-2 text-lg font-semibold text-foreground hover:bg-primary/30 hover:text-accent-foreground"
                  >
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            )
          )}
        </NavigationMenuList>
      </NavigationMenu>
    </nav>
  );
}

interface MobileMenuProps extends MenuProps {
  setMobileOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  primaryCTA: string;
  primaryCTAUrl: string;
  navbarSecondaryCTA?: string;
  navbarSecondaryCTAUrl?: string;
}

function MobileMenu({
  menuItems,
  setMobileOpen,
  primaryCTA,
  primaryCTAUrl,
  navbarSecondaryCTA,
  navbarSecondaryCTAUrl,
}: MobileMenuProps) {
  useEscapeKey(() => setMobileOpen(false));

  return (
    <FocusScope trapped>
      <div className="fixed left-0 right-0 top-[144px] z-[100] lg:hidden">
        {/* Backdrop */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="fixed inset-0 bg-black/20" onClick={() => setMobileOpen(false)} />
        {/* Menu Content */}
        <div className="relative mx-auto max-w-6xl px-4">
          <div className="rounded-2xl border-t border-gray-200 bg-card px-8 py-8 shadow-lg">
            <nav className="mb-8 flex flex-col gap-6">
              {menuItems.map(item =>
                item.isDropdown && item.dropdownItems ? (
                  <div key={item.label} className="flex flex-col">
                    <span className="mb-2 text-lg font-semibold text-foreground">{item.label}</span>
                    <div className="flex flex-col gap-2 pl-2">
                      {item.dropdownItems.map(dropdownItem => (
                        <Link
                          key={dropdownItem.slug}
                          to={dropdownItem.href}
                          className="text-base text-foreground/80 hover:text-foreground"
                          onClick={() => setMobileOpen(false)}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-lg font-semibold text-foreground hover:text-foreground/80"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              )}
              {navbarSecondaryCTA && navbarSecondaryCTAUrl && (
                <Link
                  to={navbarSecondaryCTAUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-foreground hover:text-foreground/80"
                  onClick={() =>
                    trackEvent("navbar_secondary_cta_click", { label: navbarSecondaryCTA })
                  }
                >
                  {navbarSecondaryCTA}
                </Link>
              )}
            </nav>
            <Button
              asChild
              variant="default"
              websiteStyle="modernClean"
              className="w-full md:hidden"
            >
              <Link to={primaryCTAUrl}>{primaryCTA}</Link>
            </Button>
          </div>
        </div>
      </div>
    </FocusScope>
  );
}
